import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

// Real file name + extension from the URL (e.g. 1000023998.avif)
const getFileNameFromUrl = (url: string): string => {
  const lastSegment = url.split(/[?#]/)[0].split('/').pop() || '';
  let name = lastSegment;
  try { name = decodeURIComponent(lastSegment); } catch { }
  return name || "image.jpg";
};

// Formats every share target (Line, Gallery, chooser preview…) can open as-is
const SHAREABLE_TYPES: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif' };

// Chat pictures are stored as AVIF: the Android share preview shows "?" and Line says "corrupt".
// The WebView itself can decode them (it displays them), so re-encode via canvas:
// PNG if the picture has transparent pixels (keeps them), else JPEG (photos would be 5-10x bigger as PNG).
// WebP is not used: same risk as AVIF that Line / other targets can't open it.
const hasTransparency = (ctx: CanvasRenderingContext2D, width: number, height: number): boolean => {
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
};

const toShareableImage =async (blob: Blob): Promise<{ blob: Blob; ext: string }> => {
  const ext = SHAREABLE_TYPES[blob.type];
  if (ext) return { blob, ext };

  const objectUrl = URL.createObjectURL(blob);
  let img: HTMLImageElement;
  try {
    img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Cannot decode image (' + (blob.type || 'unknown type') + ')'));
      el.src = objectUrl;
    });
  } catch (err) {
    // Can't decode here (e.g. AVIF on iOS ≤ 15): share the original file rather than fail
    URL.revokeObjectURL(objectUrl);
    console.warn('shareImage: decode failed, sharing original', err);
    return { blob, ext: (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg') };
  }

  const canvas = document.createElement('canvas');
  try {
    // iOS Safari refuses canvases over 16,777,216 px (4096x4096): scale big pictures down to fit
    const MAX_PIXELS = 16_777_216;
    const scale = Math.min(1, Math.sqrt(MAX_PIXELS / (img.naturalWidth * img.naturalHeight)));
    canvas.width = Math.floor(img.naturalWidth * scale);
    canvas.height = Math.floor(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const type = hasTransparency(ctx, canvas.width, canvas.height) ? 'image/png' : 'image/jpeg';
    if (type === 'image/jpeg') {
      // JPEG has no alpha: paint a white background under the picture (otherwise black)
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const out = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Image encode failed'))), type, 0.92));
    return { blob: out, ext: SHAREABLE_TYPES[type] };
  } finally {
    URL.revokeObjectURL(objectUrl);
    canvas.width = canvas.height = 0; // iOS: free canvas memory now (total canvas memory is capped)
  }
};

// Android picks the share MIME type from the file extension via MimeTypeMap, which gives up on
// names with Thai chars/spaces (→ "*/*"), so keep the base name ASCII-safe.
const safeFileName = (fileName: string, ext: string): string => {
  const base = fileName.replace(/\.[^.]*$/, '').replace(/[^A-Za-z0-9_\-]/g, '_').replace(/_+/g, '_');
  return (base && base !== '_' ? base : 'image_' + Date.now()) + '.' + ext;
};

const blobToBase64 =(blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(',')[1]); // strip "data:...;base64,"
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

// Share an image file (not just its link).
// Native app (Android/iOS): save into the app cache, then open the OS share sheet via @capacitor/share
//   - Android WebView has no navigator.share, so the Web Share API can't be used there.
// Web/PWA: Web Share API with a File.
// onPrepared: called once the file is ready (right before the share sheet opens, or before an error alert),
//   so the caller can hide its "preparing" indicator — Share.share() only resolves after the sheet closes.
// signal: user pressed Cancel — aborts the download, and the share sheet is never opened.
export const shareImageFromUrl = async (imageUrl: string, fileName?: string, onPrepared?: () => void, signal?: AbortSignal) => {
  fileName = fileName || getFileNameFromUrl(imageUrl);
  try {
    // Cache-bust: Safari may reuse the <img> (no-CORS) cached response for this fetch and fail CORS with "Load failed"
    const fetchUrl = imageUrl + (imageUrl.includes('?') ? '&' : '?') + '_s=' + Date.now();
    const response = await fetch(fetchUrl, { cache: 'no-store', signal });
    if (!response.ok) throw new Error('HTTP ' + response.status);
    const shareable = await toShareableImage(await response.blob());
    if (signal?.aborted) return;
    const blob = shareable.blob;
    fileName = safeFileName(fileName, shareable.ext);

    if (Capacitor.isNativePlatform()) {
      const saved = await Filesystem.writeFile({
        path: 'share/' + fileName,
        data: await blobToBase64(blob),
        directory: Directory.Cache,
        recursive: true,
      });
      if (signal?.aborted) return;
      onPrepared?.();
      await Share.share({ files: [saved.uri] });
      return;
    }

    const file = new File([blob], fileName, { type: blob.type });
    if (signal?.aborted) return;
    onPrepared?.();
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file] });
    } else {
      alert("File sharing is not supported on this device.");
    }
  } catch (error) {
    onPrepared?.();
    const msg = String((error as any)?.message ?? error);
    // User closed the share sheet: native "Share canceled", web AbortError / "...cancellation..."
    if (/cancel|abort/i.test(msg) || (error as any)?.name === 'AbortError') return;
    console.error("Error sharing image:", error);
    alert(imageUrl + ':' + msg);
  }
};
